// src/services/financial-reports.ts
import api from "./api";
import {
  ComprehensiveFinancialReport,
  FinancialReportFilters,
  FinancialReportExportOptions,
  BudgetComparison,
  FinancialKPI,
} from "../types/financial-reports";
import { ApiResponse } from "../types/common";

const REPORTS_ENDPOINT = "/financial-reports";

export const financialReportsService = {
  // Get comprehensive financial report
  async getComprehensiveReport(
    filters: FinancialReportFilters = {}
  ): Promise<ApiResponse<ComprehensiveFinancialReport>> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });

    try {
      const data: any = await api.get(
        `${REPORTS_ENDPOINT}/comprehensive?${params.toString()}`
      );
      return data;
    } catch (error) {
      console.error("Error fetching comprehensive financial report:", error);
      throw error;
    }
  },

  // Get financial dashboard summary
  async getFinancialDashboard(
    filters: FinancialReportFilters = {}
  ): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });

    try {
      const data: any = await api.get(
        `${REPORTS_ENDPOINT}/dashboard?${params.toString()}`
      );
      return data;
    } catch (error) {
      console.error("Error fetching financial dashboard:", error);
      throw error;
    }
  },

  // Get revenue analysis
  async getRevenueAnalysis(
    filters: FinancialReportFilters = {}
  ): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });

    try {
      const data: any = await api.get(
        `${REPORTS_ENDPOINT}/revenue?${params.toString()}`
      );
      return data;
    } catch (error) {
      console.error("Error fetching revenue analysis:", error);
      throw error;
    }
  },

  // Get expense analysis
  async getExpenseAnalysis(
    filters: FinancialReportFilters = {}
  ): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });

    try {
      const data: any = await api.get(
        `${REPORTS_ENDPOINT}/expenses?${params.toString()}`
      );
      return data;
    } catch (error) {
      console.error("Error fetching expense analysis:", error);
      throw error;
    }
  },

  // Get profit & loss report
  async getProfitLossReport(
    filters: FinancialReportFilters = {}
  ): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });

    try {
      const data: any = await api.get(
        `${REPORTS_ENDPOINT}/profit-loss?${params.toString()}`
      );
      return data;
    } catch (error) {
      console.error("Error fetching profit & loss report:", error);
      throw error;
    }
  },

  // Get cash flow report
  async getCashFlowReport(
    filters: FinancialReportFilters = {}
  ): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });

    try {
      const data: any = await api.get(
        `${REPORTS_ENDPOINT}/cash-flow?${params.toString()}`
      );
      return data;
    } catch (error) {
      console.error("Error fetching cash flow report:", error);
      throw error;
    }
  },

  // Get branch comparison report
  async getBranchComparison(
    filters: FinancialReportFilters = {}
  ): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });

    try {
      const data: any = await api.get(
        `${REPORTS_ENDPOINT}/branch-comparison?${params.toString()}`
      );
      return data;
    } catch (error) {
      console.error("Error fetching branch comparison:", error);
      throw error;
    }
  },

  // Get financial KPIs
  async getFinancialKPIs(
    filters: FinancialReportFilters = {}
  ): Promise<ApiResponse<FinancialKPI[]>> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });

    try {
      const data: any = await api.get(
        `${REPORTS_ENDPOINT}/kpis?${params.toString()}`
      );
      return data;
    } catch (error) {
      console.error("Error fetching financial KPIs:", error);
      throw error;
    }
  },

  // Get budget comparison
  async getBudgetComparison(
    filters: FinancialReportFilters = {}
  ): Promise<ApiResponse<BudgetComparison[]>> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });

    try {
      const data: any = await api.get(
        `${REPORTS_ENDPOINT}/budget-comparison?${params.toString()}`
      );
      return data;
    } catch (error) {
      console.error("Error fetching budget comparison:", error);
      throw error;
    }
  },

  // Export financial report
  async exportFinancialReport(
    options: FinancialReportExportOptions
  ): Promise<Blob> {
    try {
      // Use fetch directly for blob responses to avoid api interceptor issues
      const API_BASE_URL =
        import.meta.env.VITE_BACKEND_URL || "http://localhost:5000";
      const response = await fetch(
        `${API_BASE_URL}/api${REPORTS_ENDPOINT}/export`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "ngrok-skip-browser-warning": "true",
          },
          body: JSON.stringify(options),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Export failed with status ${response.status}`
        );
      }

      const blob = await response.blob();
      return blob;
    } catch (error) {
      console.error("Error exporting financial report:", error);
      throw error;
    }
  },

  // Get payment methods analysis
  async getPaymentMethodsAnalysis(
    filters: FinancialReportFilters = {}
  ): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });

    try {
      const data: any = await api.get(
        `${REPORTS_ENDPOINT}/payment-methods?${params.toString()}`
      );
      return data;
    } catch (error) {
      console.error("Error fetching payment methods analysis:", error);
      throw error;
    }
  },

  // Get financial trends
  async getFinancialTrends(
    filters: FinancialReportFilters = {}
  ): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });

    try {
      const data: any = await api.get(
        `${REPORTS_ENDPOINT}/trends?${params.toString()}`
      );
      return data;
    } catch (error) {
      console.error("Error fetching financial trends:", error);
      throw error;
    }
  },

  // Get student fee analysis
  async getStudentFeeAnalysis(
    filters: FinancialReportFilters = {}
  ): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });

    try {
      const data: any = await api.get(
        `${REPORTS_ENDPOINT}/student-fees?${params.toString()}`
      );
      return data;
    } catch (error) {
      console.error("Error fetching student fee analysis:", error);
      throw error;
    }
  },
};
